import { useState, useMemo } from 'react';
import { 
  Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import GoalTrackingChart from './GoalTrackingChart';
import ChartSlideshow from './components/analytics/ChartSlideshow';
import { getEffectiveMealTimestamp, getMealLocalDateKey, toLocalDateKey } from './utils/mealDate';

interface ClientAnalyticsProps {
  clientData: any[];
  clientName?: string;
  calculatedGoals?: {
    targetCalories: number;
    protein: { min: number; max: number; target: number };
    carbs: { min: number; max: number; target: number };
    fat: { min: number; max: number; target: number };
  } | null;
}

interface ChartData {
  date: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  meals: number;
  caloriesTarget: number;
  proteinTarget: number;
  carbsTarget: number;
  fatTarget: number;
}

export default function ClientAnalytics({ clientData, clientName, calculatedGoals }: ClientAnalyticsProps) {
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  // Process data for charts
  const chartData = useMemo(() => {
    if (!clientData.length) return [];

    const now = new Date();
    const daysToShow = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date(now.getTime() - (daysToShow - 1) * 24 * 60 * 60 * 1000);
    
    // Set start date to beginning of day
    startDate.setHours(0, 0, 0, 0);

    // Filter out bot command entries
    const filteredClientData = clientData.filter(meal => {
      const mealText = (meal.input_text || '').toLowerCase().trim();
      return !mealText.startsWith('/') && mealText !== 'start' && mealText !== '';
    });

    // Group meals by date
    const mealsByDate = new Map<string, any[]>();
    
    filteredClientData.forEach(meal => {
      const mealDate = getEffectiveMealTimestamp(meal);
      if (!mealDate) return;

      const dateKey = getMealLocalDateKey(meal);
      if (!dateKey) return;

      const mealDayStart = new Date(mealDate);
      mealDayStart.setHours(0, 0, 0, 0);
      const rangeStartKey = toLocalDateKey(startDate);

      if (dateKey >= rangeStartKey) {
        if (!mealsByDate.has(dateKey)) {
          mealsByDate.set(dateKey, []);
        }
        mealsByDate.get(dateKey)!.push(meal);
      }
    });

    // Create chart data points
    const data: ChartData[] = [];
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
      const dateKey = toLocalDateKey(date);
      const dayMeals = mealsByDate.get(dateKey) || [];

      const dayData: ChartData = {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        calories: dayMeals.reduce((sum, meal) => sum + (meal.calories || 0), 0),
        protein: dayMeals.reduce((sum, meal) => sum + (meal.protein_g || 0), 0),
        carbs: dayMeals.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0),
        fat: dayMeals.reduce((sum, meal) => sum + (meal.fat_g || 0), 0),
        meals: dayMeals.length,
        caloriesTarget: calculatedGoals?.targetCalories || 0,
        proteinTarget: calculatedGoals?.protein.target || 0,
        carbsTarget: calculatedGoals?.carbs.target || 0,
        fatTarget: calculatedGoals?.fat.target || 0
      };

      data.push(dayData);
    }

    return data;
  }, [clientData, timeRange, calculatedGoals]);

  // Calculate macro distribution for pie chart
  const macroDistribution = useMemo(() => {
    if (!clientData.length) return [];

    const now = new Date();
    const daysToShow = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date(now.getTime() - (daysToShow - 1) * 24 * 60 * 60 * 1000);
    
    // Set start date to beginning of day
    startDate.setHours(0, 0, 0, 0);

    // Filter out bot command entries and filter data for selected time range
    const filteredData = clientData.filter(meal => {
      const mealText = (meal.input_text || '').toLowerCase().trim();
      if (mealText.startsWith('/') || mealText === 'start' || mealText === '') {
        return false;
      }
      
      const mealDate = getEffectiveMealTimestamp(meal);
      if (!mealDate) return false;

      const mealDayStart = new Date(mealDate);
      mealDayStart.setHours(0, 0, 0, 0);
      return mealDayStart >= startDate;
    });

    const totalProtein = filteredData.reduce((sum, meal) => sum + (meal.protein_g || 0), 0);
    const totalCarbs = filteredData.reduce((sum, meal) => sum + (meal.carbs_g || 0), 0);
    const totalFat = filteredData.reduce((sum, meal) => sum + (meal.fat_g || 0), 0);

    return [
      { name: 'Protein', value: totalProtein, color: '#0eeaff' },
      { name: 'Carbs', value: totalCarbs, color: '#8aff8a' },
      { name: 'Fat', value: totalFat, color: '#ff8a8a' }
    ].filter(item => item.value > 0);
  }, [clientData, timeRange]);

  // Calculate consistency data
  const calculateConsistency = (data: any[]) => {
    const totalDays = data.length;
    const daysWithData = data.filter(d => d.calories > 0).length;
    return totalDays > 0 ? (daysWithData / totalDays) * 100 : 0;
  };

  // Show message if no data
  if (!clientData.length) {
    return (
      <div className="space-y-6 pb-20 md:pb-6">
        <div className="retro-card p-4 md:p-6">
          <div className="index-tab">
            <h3 className="text-lg font-semibold text-text-dark">
              📈 Analytics - {clientName || 'Client'}
            </h3>
          </div>
          <div className="p-3 md:p-4">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-lg font-semibold text-text-dark mb-2">
                No Analytics Data Yet
              </h3>
              <p className="text-text-medium mb-6">
                {clientName || 'This client'} hasn't logged any meals yet. 
                Once they start logging meals, you'll see detailed analytics and trends here.
              </p>
              <div className="bg-gradient-to-r from-[#0eeaff]/10 to-[#8aff8a]/10 rounded-lg p-4 border border-[#d4d4d4]">
                <h4 className="font-semibold text-text-dark mb-2">What you'll see here:</h4>
                <ul className="text-sm text-text-medium space-y-1">
                  <li>• Daily calorie intake trends</li>
                  <li>• Macro distribution charts</li>
                  <li>• Consistency tracking</li>
                  <li>• Goal performance metrics</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="retro-card">
      <div className="index-tab">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">
            📈 Analytics - {clientName || 'Client'}
          </h3>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6 pb-32 md:pb-12">
        {/* Time Range Selector */}
        <div className="flex justify-center">
          <div className="flex space-x-2 bg-white rounded-lg p-1 shadow">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-[#0eeaff] text-[#1f1f1f] shadow'
                    : 'text-[#2d2d2d] hover:bg-[#f8f4e6]'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>
        </div>



        {/* Chart Slideshow */}
        <ChartSlideshow 
          chartData={chartData}
          calculatedGoals={calculatedGoals}
        />

        {/* Macro Distribution Chart */}
        {macroDistribution.length > 0 && (
          <div className="bg-white rounded-lg p-4 shadow">
            <h4 className="text-lg font-semibold text-[#1f1f1f] mb-4 text-center">
              🍽️ Macro Distribution (Total)
            </h4>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={macroDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {macroDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d4d4d4',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value: number, name: string) => [`${value}g`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center space-x-4 mt-4">
              {macroDistribution.map((macro) => (
                <div key={macro.name} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: macro.color }}
                  ></div>
                  <span className="text-sm text-[#2d2d2d]">{macro.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Goal Tracking Chart */}
        <GoalTrackingChart 
          clientData={clientData} 
          calculatedGoals={calculatedGoals} 
          timeRange={timeRange} 
        />

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-[#8aff8a]/20 to-[#0eeaff]/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#8aff8a]">
              {chartData.filter(d => d.calories > 0).length}
            </div>
            <div className="text-sm text-[#2d2d2d]">Days Logged</div>
          </div>
          <div className="bg-gradient-to-br from-[#0eeaff]/20 to-[#8aff8a]/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#0eeaff]">
              {Math.round(chartData.reduce((sum, d) => sum + d.calories, 0) / chartData.length)}
            </div>
            <div className="text-sm text-[#2d2d2d]">Avg Calories/Day</div>
          </div>
          <div className="bg-gradient-to-br from-[#8aff8a]/20 to-[#ff8a8a]/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#8aff8a]">
              {Math.round(calculateConsistency(chartData))}%
            </div>
            <div className="text-sm text-[#2d2d2d]">Consistency</div>
          </div>
          <div className="bg-gradient-to-br from-[#ff8a8a]/20 to-[#0eeaff]/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-[#ff8a8a]">
              {Math.max(...chartData.map(d => d.calories))}
            </div>
            <div className="text-sm text-[#2d2d2d]">Peak Calories</div>
          </div>
        </div>
      </div>
    </div>
  );
} 